export const t = {
  EN: {
    nav: {
      about: "About",
      renewable: "Renewable Energy",
      financing: "Financing",
      bess: "BESS Gigafactory",
      projects: "Projects",
      news: "News",
      contact: "Contact",
      holyGrail: "Holy Grail",
      ciEnergy: "C&I Energy",
      gpTransformers: "TRANSFORMERS",
    },
    hero: {
      label: "Leading European Energy Holding",
      title: "Powering Tomorrow's Energy Infrastructure",
      subtitle: "KÖBER Energy Holding pioneers renewable energy development, strategic project financing, and the future of battery energy storage at gigascale.",
      cta: "Get in Touch",
      cta2: "Explore Projects",
      deckCta: "Download Investment Deck",
    },
    about: {
      label: "About Us",
      title: "A New Standard in Energy Holding",
      body: "KÖBER Energy Holding is a start-up company specialised in renewable, clean, and green energy infrastructure with a focus on BESS projects, energy efficiency, and energy production, focusing on delivering baseload clean and green energy through an innovative energy unit.",
      stat1: { value: "3+ GW", label: "Renewable Pipeline" },
      stat2: { value: "€550M", label: "Assets Under Development" },
      stat3: { value: "12+", label: "Countries Active" },
      stat4: { value: "2030", label: "Net-Zero Commitment" },
    },
    renewable: {
      label: "Renewable Energy",
      title: "From Land to Grid — Full-Cycle Development",
      subtitle: "We manage the entire lifecycle of renewable energy projects, from site acquisition and permitting to construction and operational management.",
      cards: [
        { icon: "Sun", title: "BESS Projects", body: "Utility-scale BESS and hybrid projects starting from 10 MW to 500 MW across Europe's most important energy corridors." },
        { icon: "Zap", title: "Hybrid Projects", body: "Co-located solar + wind + BESS hybrid assets maximizing capacity factors and revenue streams." },
        { icon: "Globe", title: "Biogas Projects", body: "Early-stage biogas production integrated with renewable generation for industrial decarbonization." },
      ],
      om: {
        label: "Operations & Maintenance",
        title: "From COD to O&M through Eligible Partners",
        subtitle: "Once a project reaches Commercial Operation Date, KÖBER Energy Holding ensures long-term asset performance through a curated network of certified O&M partners — protecting yield, uptime, and investor returns.",
        flow: [
          { step: "01", label: "Development", desc: "Site acquisition, permitting, grid connection" },
          { step: "02", label: "Construction", desc: "EPC contractor management, quality assurance" },
          { step: "03", label: "COD", desc: "Commercial Operation Date — revenue generation begins" },
          { step: "04", label: "O&M", desc: "Ongoing operations through certified partner network" },
        ],
        services: [
          { icon: "Activity", title: "Performance Monitoring", body: "24/7 SCADA-based asset monitoring with real-time yield, availability, and performance ratio tracking." },
          { icon: "Wrench", title: "Preventive & Corrective Maintenance", body: "Scheduled preventive maintenance and rapid corrective intervention protocols to minimise downtime." },
          { icon: "TrendingUp", title: "Yield Optimisation", body: "Continuous production analysis and technical audits to maximise energy yield across the asset lifetime." },
          { icon: "Award", title: "Partner Certification", body: "Rigorous vetting and certification of O&M service providers to ensure bankable, insurance-grade standards." },
        ],
      },
    },
    financing: {
      label: "Project Financing",
      title: "Structured Finance for the Energy Transition",
      subtitle: "We architect bespoke financing solutions combining equity, senior debt, and green bonds to accelerate project delivery.",
      features: [
        { title: "Tailor Made Financing Solutions", body: "Our unique approach of thinking out of the box allows us to facilitate for our clients tailor-made financing solutions." },
        { title: "COD Project Financing", body: "Debt and equity structured around Commercial Operation Date milestones — unlocking capital the moment a project transitions from construction to revenue-generating operations." },
        { title: "C&I Energy Solutions", body: "Köber Energy finances, installs, and operates the photovoltaic system at the client premises and delivers to the client a long-term PPA at a fixed price." },
        { title: "Mezzanine & Bridging", body: "Flexible subordinated capital solutions bridging construction and operational phases." },
        { title: "PPA Structuring", body: "Long-term Power Purchase Agreement advisory and counterparty matching for bankable offtake." },
        { title: "M&A Advisory", body: "Buy-side and sell-side mandates across the full renewable energy M&A lifecycle." },
      ],
    },
    bess: {
      label: "BESS Gigafactory",
      title: "The Next Generation Battery Gigafactory",
      subtitle: "KÖBER Energy Holding is developing Central Europe's largest Battery Energy Storage System manufacturing facility — a strategic asset for the continent's energy security.",
      stats: [
        { value: "36 GWh", label: "Partners Annual Production Capacity" },
        { value: "44,000 sqm", label: "New Assembly Lines" },
        { value: "2027", label: "Operational Target" },
        { value: "€1.8B", label: "Investment Volume" },
      ],
      features: [
        { icon: "Battery", title: "LFP & NMC Technology", body: "State-of-the-art lithium iron phosphate and NMC cell production lines." },
        { icon: "Factory", title: "Full Vertical Integration", body: "From cell manufacturing to module assembly and system integration." },
        { icon: "Leaf", title: "Zero-Carbon Manufacturing", body: "100% renewable-powered production with closed-loop recycling." },
        { icon: "Shield", title: "EU Supply Chain Security", body: "Reducing dependency on Asian supply chains for European energy storage." },
      ],
    },
    projects: {
      label: "Projects",
      title: "Our Portfolio",
      subtitle: "A growing portfolio of landmark energy assets across Central and Eastern Europe.",
      items: [
        { title: "Braila BESS, PCS, and Transformers Gigafactory", location: "Brăila, Romania", type: "Transformers & BESS Gigafactory — 4 GW", capacity: "4 GW", status: "Investment Q3 2026" },
        { title: "The Holy Grail Unit", location: "Global Deployment", type: "Independent Power Generation", capacity: "Off-Grid | 24/7 Baseload", status: "Available Now", link: "/holy-grail" },
        { title: "Pelicanu 4 Project", location: "Romania", type: "BESS Stand-alone", capacity: "135 MWh", status: "RTB" },
        { title: "Iaz 3 Project", location: "Romania", type: "BESS Stand-alone", capacity: "196 MWh", status: "RTB" },
        { title: "Biogas Project", location: "Romania", type: "Gas to Power", capacity: "6,000 ha", status: "Under Development" },
        { title: "Iaz 2 Project", location: "Romania", type: "BESS Stand-alone", capacity: "196 MWh", status: "RTB" },
        { title: "Simnicu de Sus Project", location: "Romania", type: "BESS Stand-alone", capacity: "300 MWh", status: "RTB" },
        { title: "Rosiesti Project", location: "Romania", type: "BESS Stand-alone", capacity: "75 MWh", status: "Permitting Phase" },
        { title: "Alba Iulia 1 Project", location: "Romania", type: "BESS Stand-alone", capacity: "140 MWh", status: "RTB" },
        { title: "Alba Iulia 2 Project", location: "Romania", type: "BESS Stand-alone", capacity: "140 MWh", status: "RTB" },
        { title: "Ezeris 2 Project", location: "Romania", type: "BESS Stand-alone", capacity: "300 MWh", status: "RTB" },
        { title: "Pelicanu 3 Project", location: "Romania", type: "BESS Stand-alone", capacity: "450 MWh", status: "RTB" },
        { title: "Vulcan Hybrid Project", location: "Romania", type: "Hybrid Solar+BESS", capacity: "33 MW PV + 120 MWh BESS", status: "RTB" },
        { title: "Ovidiu 2 Project", location: "Romania", type: "BESS Stand-alone", capacity: "135.4 MWh", status: "RTB" },
        { title: "Ramnicu Sarat Project", location: "Romania", type: "BESS Stand-alone", capacity: "330 MWh", status: "RTB" },
        { title: "Vanatori Project", location: "Romania", type: "Solar PV", capacity: "90 MW", status: "RTB" },
        { title: "AI Data Centre 1", location: "Romania", type: "AI Data Centre", capacity: "400 MWh IT", status: "Under Development" },
        { title: "AI Data Centre 2", location: "Romania", type: "AI Data Centre", capacity: "1.2 GWh IT", status: "Under Development" },
      ],
    },
    team: {
      label: "Leadership",
      title: "The Team Behind the Vision",
      subtitle: "Our leadership combines deep energy sector expertise with world-class financial and engineering capabilities.",
      members: [
        { name: "Alexander Köber", role: "Founder & CEO", bio: "20+ years in European energy markets, former partner at a leading infrastructure fund." },
        { name: "Mihai Ionescu", role: "Chief Investment Officer", bio: "Ex-EBRD energy specialist with €4B+ in structured transactions across CEE." },
        { name: "Dr. Petra Varga", role: "Head of BESS Technology", bio: "PhD in Electrochemistry, 15 years developing advanced battery systems for grid-scale applications." },
        { name: "Sophie Berglund", role: "Chief Financial Officer", bio: "Former Goldman Sachs infrastructure finance, specialist in green bonds & project finance." },
      ],
    },
    news: {
      label: "News & Insights",
      title: "Latest from KÖBER Energy Holding",
      subtitle: "Industry perspectives, project milestones and market intelligence from our team.",
      filters: [
        { key: "all",     label: "All" },
        { key: "market",  label: "Market News" },
        { key: "romania", label: "Romania" },
        { key: "kober",   label: "KÖBER Updates" },
      ],
      items: [
        {
          category: "market", date: "April 2026", tag: "Market News",
          title: "European BESS Deployments Hit Record 12 GW in 2025",
          body: "Grid-scale battery storage reached a historic milestone across the EU, driven by aggressive renewable integration targets and sharply falling LFP cell costs.",
        },
        {
          category: "market", date: "March 2026", tag: "Market News",
          title: "LFP Battery Prices Drop Below €80/kWh — Parity with Gas Peakers Achieved",
          body: "Lithium iron phosphate cell costs have fallen sharply, making standalone BESS projects economically competitive with gas-fired peak generation for the first time.",
        },
        {
          category: "market", date: "February 2026", tag: "Market News",
          title: "EU Battery Regulation Mandates 70% Recycled Content by 2030",
          body: "The latest traceability requirements accelerate the shift toward domestically manufactured LFP cells and circular battery supply chains across Europe.",
        },
        {
          category: "romania", date: "May 2026", tag: "Romania",
          title: "Romania Surpasses 1 GW Installed Wind Capacity in Q1 2026",
          body: "Romania's onshore wind fleet crosses the gigawatt milestone, reinforcing its position as a key Central European market for large-scale battery storage co-location.",
        },
        {
          category: "romania", date: "March 2026", tag: "Romania",
          title: "Transelectrica Opens 800 MW Grid Flexibility Tender for Storage",
          body: "Romania's transmission operator issued a capacity tender targeting battery storage and demand-response assets to stabilise frequency regulation across the national grid.",
        },
        {
          category: "romania", date: "January 2026", tag: "Romania",
          title: "EU Modernisation Fund Allocates €4.2B to Romania's Energy Transition",
          body: "The largest single EU grant in Romanian energy history targets grid upgrades, storage integration, and clean hydrogen pilots — with disbursements running through 2030.",
        },
        {
          category: "kober", date: "October 2026", tag: "KÖBER Update",
          title: "Alba Iulia 1 & 2 Standalone BESS Projects Enter Final Permitting Phase",
          body: "The combined 280 MWh BESS development has secured its Building Permit and financial close, advancing toward Grid Connection Agreement and Establishment Permit.",
        },
        {
          category: "kober", date: "October 2026", tag: "KÖBER Update",
          title: "Râmnicu Sărat Standalone BESS Project Enters Permitting Phase",
          body: "The 330 MW BESS development has secured its Grid Connection Agreement and financial close, advancing toward Building Permit.",
        },
        {
          category: "kober", date: "October 2026", tag: "KÖBER Update",
          title: "Vulcan Hybrid Project Enters NTP Phase",
          body: "The combined 33 MW PV and 120 MW BESS development has secured its Building Permit and financial close, advancing toward Notice to Proceed and construction commencement.",
        },
        {
          category: "kober", date: "May 2026", tag: "KÖBER Update",
          title: "Braila BESS Gigafactory Site Confirmed in Brăila County",
          body: "KÖBER Energy Holding confirms the strategic location for its 4 GW annual-capacity Gigafactory, with the investment decision and construction timeline locked in for Q3 2026.",
        },
        {
          category: "kober", date: "April 2026", tag: "KÖBER Update",
          title: "Pelicanu 3 & 4 BESS Projects Enter Permitting Phase",
          body: "The combined 585 MWh standalone BESS development has secured its grid connection study approval, advancing toward Building Permit stage and financial close.",
        },
        {
          category: "kober", date: "September 2026", tag: "KÖBER Update",
          title: "Romania BESS Standalone 3.3 GWh Reaches Ready-to-Build Status",
          body: "KÖBER Energy Holding's flagship standalone battery storage project achieves RTB milestone, positioning the asset for financial close and construction commencement in H2 2026.",
        },
      ],
    },
    partners: {
      label: "Partners & Investors",
      title: "Trusted by Leading Institutions",
    },
    contact: {
      title: "Get in Touch",
      subtitle: "Interested in co-investment, project partnerships, or learning more about KÖBER Energy Holding? Reach out to our team.",
      name: "Full Name",
      email: "Email Address",
      interest: "Area of Interest",
      message: "Message",
      interestOptions: ["Renewable Energy Investment", "Project Financing", "BESS / Gigafactory", "Partnership", "Other"],
      send: "Send Message",
      success: "Message sent! Our team will be in touch within 24 hours.",
    },
    footer: {
      tagline: "Pioneering the energy transition across Central & Eastern Europe.",
      links: { company: "Company", investors: "Investors", legal: "Legal" },
      copyright: "© KÖBER Energy Holding. All rights reserved.",
    },
    cookieBanner: {
      title: "We use cookies",
      body: "We use cookies to improve your experience, analyse site traffic, and personalise content. You may accept all, reject non-essential cookies, or manage your preferences.",
      acceptAll: "Accept All",
      rejectAll: "Reject All",
      manage: "Manage",
      save: "Save Preferences",
      policyLink: "Cookie Policy",
      necessary: { title: "Necessary", desc: "Essential for core website functionality. Cannot be disabled." },
      analytics: { title: "Analytics", desc: "Help us understand how visitors interact with the website." },
      marketing: { title: "Marketing", desc: "Used to deliver personalised content and measure campaign effectiveness." },
    },
    holyGrail: {
      hero: {
        label: "KÖBER Energy Holding — Breakthrough Technology",
        title: "The Holy Grail of Renewable Energy",
        subtitle: "An independent power generation unit delivering limitless, continuous, efficient, affordable, clean energy — available 24/7 from the moment it is activated.",
      },
      mission: {
        label: "Our Breakthrough",
        title: "We Solved What Others Couldn't",
        body: "We, KÖBER ENERGY HOLDING SRL, have created the \"Holy Grail\" of renewable energy. We have addressed and solved the major issues that current technologies are facing, and we have developed an independent power generation unit that provides limitless, continuous, efficient, affordable, clean energy, ready for mass scaling.",
        integration: "The unit can be deployed as a standalone system or integrated with storage solutions, providing constant, clean energy available 24/7 from the moment it starts operating.",
      },
      pillars: [
        { title: "Deploy Anywhere", body: "Our unit can be deployed anywhere and everywhere — urban rooftops, industrial zones, or the most remote locations on Earth.", icon: "Globe" },
        { title: "Extreme Environments", body: "It can operate in extreme environments where conventional renewable technology fails or cannot be deployed.", icon: "Thermometer" },
        { title: "Instant Power", body: "It starts generating electricity from the very second it is activated. Zero warm-up time. Zero delay.", icon: "Zap" },
      ],
      specs: [
        { label: "Unit Size", value: "1MWh", desc: "20 ft container + 3X Standalone BESS, additional size with storage" },
        { label: "Warranty", value: "10 Years", desc: "Full warranty on all components" },
        { label: "Operation", value: "Silent", desc: "Zero acoustic impact" },
        { label: "Maintenance", value: "Minimal", desc: "Lowest operational cost" },
        { label: "Cost Target", value: "< €0.05/kWh", desc: "Our long-term price target" },
        { label: "Current Rate", value: "€0.063/kWh", desc: "Per kWh — 10-yr operational leasing" },
      ],
      applications: {
        label: "Applications",
        title: "Powering the Industries That Can't Stop",
        subtitle: "24/7 baseload energy supply for the world's most demanding power consumers.",
        items: [
          { title: "AI & Data Centers", body: "Continuous, high-density power for hyperscale AI and cloud computing facilities. Guaranteed 24/7 uptime with zero grid dependency." },
          { title: "Heavy Industry & Siderurgy", body: "Reliable baseload for steel mills, smelters, and energy-intensive manufacturing processes that cannot tolerate power interruptions." },
          { title: "EV Charging & Grid Balancing", body: "Standalone EV charging deployable anywhere without grid dependency, plus balancing solutions for national grid operators." },
        ],
      },
      environment: {
        label: "Environmental Impact",
        title: "Zero Impact. By Design.",
        items: [
          "Zero CO₂ emission", "Zero impact on the environment",
          "No chemical usage", "No fire or heating exposure",
          "No risk of explosion", "Easy maintenance",
          "Low weight", "Easy installation",
        ],
      },
      grid: {
        title: "Off-Grid Freedom & Grid Balancing",
        body: "Our unit is fundamentally an off-grid solution designed for self-consumption, significantly reducing reliance on the national grid. Units can also be deployed as balancing solutions for national grid operators.",
        aim: "Our aim: reduce the cost of energy to less than €0.05/kWh.",
      },
      bess: "* BESS (Battery Energy Storage System) integration available — enabling continuous, dispatchable clean energy 24/7.",
      cta: {
        title: "Interested in Deploying the Holy Grail Unit?",
        subtitle: "Contact our team to discuss deployment options, leasing terms, and strategic partnership opportunities.",
        btn: "Get in Touch",
      },
    },
  },
  RO: {
    nav: {
      about: "Despre Noi",
      renewable: "Energie Regenerabilă",
      financing: "Finanțare",
      bess: "Gigafabrica BESS",
      projects: "Proiecte",
      news: "Știri",
      contact: "Contact",
      holyGrail: "Holy Grail",
      ciEnergy: "Soluții C&I",
      gpTransformers: "TRANSFORMERS",
    },
    hero: {
      label: "Holding Energetic European de Top",
      title: "Alimentăm Infrastructura Energetică a Viitorului",
      subtitle: "KÖBER Energy Holding este pionier în dezvoltarea energiei regenerabile, finanțarea strategică a proiectelor și viitorul stocării energiei la gigascară.",
      cta: "Contactați-ne",
      cta2: "Explorați Proiectele",
      deckCta: "Descărcați Prospectul de Investiții",
    },
    about: {
      label: "Despre Noi",
      title: "Un Nou Standard în Holding Energetic",
      body: "KÖBER Energy Holding este o companie start-up specializată în infrastructura energetică regenerabilă, curată și verde, cu accent pe proiectele BESS, eficiența energetică și producția de energie, concentrată pe livrarea de energie curată și verde de tip baseload printr-o unitate energetică inovatoare.",
      stat1: { value: "3+ GW", label: "Pipeline Regenerabil" },
      stat2: { value: "€550M", label: "Active în Dezvoltare" },
      stat3: { value: "12+", label: "Țări Active" },
      stat4: { value: "2030", label: "Angajament Net-Zero" },
    },
    renewable: {
      label: "Energie Regenerabilă",
      title: "De la Teren la Rețea — Dezvoltare Completă",
      subtitle: "Gestionăm întregul ciclu de viață al proiectelor de energie regenerabilă, de la achiziția terenului și autorizare până la construcție și management operațional.",
      cards: [
        { icon: "Sun", title: "Proiecte BESS", body: "Proiecte BESS la scară utilitară, începând de la 10 MW până la 500 MW, în cele mai importante coridoare energetice din Europa." },
        { icon: "Zap", title: "Proiecte Hibride", body: "Active hibride solar + BESS co-localizate, maximizând factorii de capacitate și fluxurile de venituri." },
        { icon: "Globe", title: "Proiecte Biogaz", body: "Proiect producție de biogaz în stadiu incipient, integrată cu generarea regenerabilă pentru decarbonizarea industrială." },
      ],
      om: {
        label: "Operare & Mentenanță",
        title: "De la COD la O&M prin parteneri eligibili",
        subtitle: "Odată ce un proiect atinge faza de Punere în Funcțiune (PiF), KÖBER Energy Holding asigură performanța pe termen lung a activelor printr-o rețea selectată de parteneri O&M certificați — protejând producția, disponibilitatea și randamentul investitorilor.",
        flow: [
          { step: "01", label: "Dezvoltare", desc: "Achiziție teren, autorizare, racordare la rețea" },
          { step: "02", label: "Construcție", desc: "Management contractori EPC, asigurarea calității" },
          { step: "03", label: "PiF", desc: "Punerea în Funcțiune — generarea de venituri începe" },
          { step: "04", label: "O&M", desc: "Operare continuă prin rețeaua de parteneri certificați" },
        ],
        services: [
          { icon: "Activity", title: "Monitorizare Performanță", body: "Monitorizare active SCADA 24/7 cu urmărire în timp real a producției, disponibilității și ratei de performanță." },
          { icon: "Wrench", title: "Mentenanță Preventivă & Corectivă", body: "Mentenanță preventivă programată și protocoale de intervenție corectivă rapidă pentru minimizarea timpilor de nefuncționare." },
          { icon: "TrendingUp", title: "Optimizare Producție", body: "Analiză continuă a producției și audituri tehnice pentru maximizarea randamentului energetic pe durata de viață a activului." },
          { icon: "Award", title: "Certificarea Partenerilor", body: "Verificare riguroasă și certificare a furnizorilor de servicii O&M pentru standarde bancabile și conformitate cu asigurările." },
        ],
      },
    },
    financing: {
      label: "Finanțare Proiecte",
      title: "Finanțare Structurată pentru Tranziția Energetică",
      subtitle: "Concepem soluții de finanțare personalizate, combinând capital propriu, datorie senior și obligațiuni verzi pentru accelerarea proiectelor.",
      features: [
        { title: "Soluții de Finanțare Personalizate", body: "Abordarea noastră unică de a gândi în afara tiparelor ne permite să facilităm pentru clienții noștri soluții de finanțare personalizate." },
        { title: "Finanțare la COD", body: "Datorie și capital structurate în jurul etapelor de Punere în Funcțiune (COD) — deblocând finanțarea în momentul în care proiectul trece de la construcție la operarea generatoare de venituri." },
        { title: "Soluții Energetice C&I", body: "Köber Energy finanțează, instalează și operează sistemul fotovoltaic la sediul clientului și livrează clientului un PPA pe termen lung la preț fix." },
        { title: "Mezanin & Finanțare Punte", body: "Soluții flexibile de capital subordonat care acoperă fazele de construcție și operaționale." },
        { title: "Structurare PPA", body: "Consultanță pentru Acorduri de Achiziție a Energiei pe termen lung și potrivire cu contrapărți bancabile." },
        { title: "Consultanță M&A", body: "Mandate de cumpărare și vânzare pe întregul ciclu M&A în energie regenerabilă." },
      ],
    },
    bess: {
      label: "Gigafabrica BESS",
      title: "Gigafabrica de Baterii de Nouă Generație",
      subtitle: "KÖBER Energy Holding dezvoltă cel mai mare complex de producție BESS din Europa Centrală — un activ strategic pentru securitatea energetică a continentului.",
      stats: [
        { value: "36 GWh", label: "Capacitate Anuală de Producție Parteneri" },
        { value: "44.000 sqm", label: "Noi Linii de Asamblare" },
        { value: "2027", label: "Țintă Operațională" },
        { value: "€1,8 Mld", label: "Volum de Investiții" },
      ],
      features: [
        { icon: "Battery", title: "Tehnologie LFP & NMC", body: "Linii de producție de celule litiu fier fosfat și NMC de ultimă generație." },
        { icon: "Factory", title: "Integrare Verticală Completă", body: "De la producția de celule la asamblarea modulelor și integrarea sistemelor." },
        { icon: "Leaf", title: "Producție Zero Carbon", body: "Producție alimentată 100% cu energie regenerabilă și reciclare în buclă închisă." },
        { icon: "Shield", title: "Securitatea Lanțului de Aprovizionare UE", body: "Reducerea dependenței de lanțurile de aprovizionare asiatice pentru stocarea energiei europene." },
      ],
    },
    projects: {
      label: "Proiecte",
      title: "Portofoliul Nostru",
      subtitle: "Un portofoliu în creștere de active energetice de referință în Europa Centrală și de Est.",
      items: [
        { title: "Gigafabrica Braila BESS, PCS și Transformatoare", location: "Brăila, România", type: "Transformatoare & Gigafabrica BESS — 4 GW", capacity: "4 GW", status: "Investiție T3 2026" },
        { title: "Unitatea Holy Grail", location: "Implementare Globală", type: "Generare Independentă de Energie", capacity: "Off-Grid | Sarcină Bază 24/7", status: "Disponibil Acum", link: "/holy-grail" },
        { title: "Proiect Pelicanu 4", location: "România", type: "BESS Standalone", capacity: "135 MWh", status: "RTB" },
        { title: "Proiect Iaz 3", location: "România", type: "BESS Standalone", capacity: "196 MWh", status: "RTB" },
        { title: "Proiect Biogaz", location: "România", type: "Gaz la Energie", capacity: "6.000 ha", status: "În Dezvoltare" },
        { title: "Proiect Iaz 2", location: "România", type: "BESS Standalone", capacity: "196 MWh", status: "RTB" },
        { title: "Proiect Simnicu de Sus", location: "România", type: "BESS Stand-alone", capacity: "300 MWh", status: "RTB" },
        { title: "Proiect Rosiesti", location: "România", type: "BESS Stand-alone", capacity: "75 MWh", status: "Faza de Autorizare" },
        { title: "Proiect Alba Iulia 1", location: "România", type: "BESS Stand-alone", capacity: "140 MWh", status: "RTB" },
        { title: "Proiect Alba Iulia 2", location: "România", type: "BESS Stand-alone", capacity: "140 MWh", status: "RTB" },
        { title: "Proiect Ezeris 2", location: "România", type: "BESS Stand-alone", capacity: "300 MWh", status: "RTB" },
        { title: "Proiect Pelicanu 3", location: "România", type: "BESS Stand-alone", capacity: "450 MWh", status: "RTB" },
        { title: "Proiect Hibrid Vulcan", location: "România", type: "Hibrid Solar+BESS", capacity: "33 MW PV + 120 MWh BESS", status: "RTB" },
        { title: "Proiect Ovidiu 2", location: "România", type: "BESS Stand-alone", capacity: "135,4 MWh", status: "RTB" },
        { title: "Proiect Râmnicu Sărat", location: "România", type: "BESS Stand-alone", capacity: "330 MWh", status: "RTB" },
        { title: "Proiect Vânători", location: "România", type: "Solar PV", capacity: "90 MW", status: "RTB" },
        { title: "Centru de Date AI 1", location: "România", type: "Centru de Date AI", capacity: "400 MWh IT", status: "În Dezvoltare" },
        { title: "Centru de Date AI 2", location: "România", type: "Centru de Date AI", capacity: "1,2 GWh IT", status: "În Dezvoltare" },
      ],
    },
    team: {
      label: "Conducere",
      title: "Echipa din Spatele Viziunii",
      subtitle: "Conducerea noastră combină expertiza profundă în sectorul energetic cu capacități financiare și de inginerie de clasă mondială.",
      members: [
        { name: "Alexander Köber", role: "Fondator & CEO", bio: "20+ ani în piețele energetice europene, fost partener al unui fond de infrastructură de top." },
        { name: "Mihai Ionescu", role: "Director de Investiții", bio: "Fost specialist energetic BERD cu peste €4 miliarde în tranzacții structurate în ECE." },
        { name: "Dr. Petra Varga", role: "Director Tehnologie BESS", bio: "Doctor în Electrochimie, 15 ani de experiență în dezvoltarea sistemelor avansate de baterii." },
        { name: "Sophie Berglund", role: "Director Financiar", bio: "Fosta Goldman Sachs finanțare infrastructură, specialist în obligațiuni verzi și finanțarea proiectelor." },
      ],
    },
    news: {
      label: "Știri & Perspective",
      title: "Ultimele Noutăți de la KÖBER Energy Holding",
      subtitle: "Perspective din industrie, etape de proiecte și informații de piață din partea echipei noastre.",
      filters: [
        { key: "all",     label: "Toate" },
        { key: "market",  label: "Știri de Piață" },
        { key: "romania", label: "România" },
        { key: "kober",   label: "Actualizări KÖBER" },
      ],
      items: [
        {
          category: "market", date: "Aprilie 2026", tag: "Știri de Piață",
          title: "Implementările BESS în Europa Ating Recordul de 12 GW în 2025",
          body: "Stocarea la scară de utilitate a atins un prag istoric în UE, impulsionată de obiectivele agresive de integrare a energiei regenerabile și de scăderea costurilor celulelor LFP.",
        },
        {
          category: "market", date: "Martie 2026", tag: "Știri de Piață",
          title: "Prețurile Bateriilor LFP Scad Sub €80/kWh — Paritate cu Centralele pe Gaz",
          body: "Costurile celulelor litiu fier fosfat au scăzut semnificativ, făcând proiectele BESS standalone competitive din punct de vedere economic cu capacitatea de vârf pe gaz.",
        },
        {
          category: "market", date: "Februarie 2026", tag: "Știri de Piață",
          title: "Regulamentul UE privind Bateriile Impune 70% Conținut Reciclat până în 2030",
          body: "Cerințele de trasabilitate accelerează tranziția spre celule LFP fabricate local și lanțuri de aprovizionare circulare pentru baterii în întreaga Europă.",
        },
        {
          category: "romania", date: "Mai 2026", tag: "România",
          title: "România Depășește 1 GW Capacitate Eoliană Instalată în T1 2026",
          body: "Parcul eolian onshore al României depășește pragul gigawatt, consolidând poziția sa ca piață cheie în tranziția energetică Central-Europeană pentru co-localizarea cu stocarea.",
        },
        {
          category: "romania", date: "Martie 2026", tag: "România",
          title: "Transelectrica Lansează Licitație de Flexibilitate Grid de 800 MW pentru Stocare",
          body: "Operatorul de transport a emis o licitație care vizează activele de stocare și răspunsul la cerere pentru stabilizarea frecvenței pe rețeaua națională.",
        },
        {
          category: "romania", date: "Ianuarie 2026", tag: "România",
          title: "Fondul de Modernizare UE Alocă €4,2 Miliarde Tranziției Energetice a României",
          body: "Cea mai mare alocare UE din istoria energetică a României vizează modernizarea rețelei, integrarea stocării și piloți de hidrogen curat cu plăți până în 2030.",
        },
        {
          category: "kober", date: "Octombrie 2026", tag: "Actualizare KÖBER",
          title: "Proiectul Alba Iulia 1 & 2 BESS Standalone Intră în Faza Finală de Autorizare",
          body: "Dezvoltarea combinată de 280 MWh BESS a obținut Autorizația de Construcție și închiderea financiară, avansând spre Contractul de Racordare și Autorizația de Înființare.",
        },
        {
          category: "kober", date: "Octombrie 2026", tag: "Actualizare KÖBER",
          title: "Proiectul Râmnicu Sărat BESS Standalone Intră în Faza de Autorizare",
          body: "Dezvoltarea combinată de 330 MW BESS a obținut Contractul de Racordare și închiderea financiară, avansând spre Autorizația de Construcție.",
        },
        {
          category: "kober", date: "Octombrie 2026", tag: "Actualizare KÖBER",
          title: "Proiectul Hibrid Vulcan Intră în Faza de NTP",
          body: "Dezvoltarea combinată de 33 MW PV și 120 MW BESS a obținut Autorizarea de Construire și închiderea financiară, avansând spre Notice to Proceed și demararea construcției.",
        },
        {
          category: "kober", date: "Mai 2026", tag: "Actualizare KÖBER",
          title: "Situl Gigafabricii Braila BESS Confirmat în Județul Brăila",
          body: "KÖBER Energy Holding confirmă locația strategică pentru Gigafabrica cu capacitate anuală de 4 GW, cu decizia de investiție și calendarul de construcție finalizate pentru T3 2026.",
        },
        {
          category: "kober", date: "Aprilie 2026", tag: "Actualizare KÖBER",
          title: "Proiectele BESS Pelicanu 3 & 4 Intră în Faza de Autorizare",
          body: "Dezvoltarea combinată de 485 MW BESS standalone a obținut aprobarea studiului de racordare, avansând spre faza de Autorizare de Construire și închiderea financiară.",
        },
        {
          category: "kober", date: "Septembrie 2026", tag: "Actualizare KÖBER",
          title: "BESS Standalone România 3,3 GWh Atinge Stadiul Ready-to-Build",
          body: "Proiectul emblematic de stocare standalone al KÖBER Energy Holding atinge etapa RTB, poziționând activul pentru închiderea financiară și demararea construcției în H2 2026.",
        },
      ],
    },
    partners: {
      label: "Parteneri & Investitori",
      title: "De Încredere pentru Instituțiile de Top",
    },
    contact: {
      title: "Contactați-ne",
      subtitle: "Ești interesat de co-investiții, parteneriate de proiecte sau vrei să afli mai multe despre KÖBER Energy Holding? Contactează echipa noastră.",
      name: "Nume Complet",
      email: "Adresă de Email",
      interest: "Domeniu de Interes",
      message: "Mesaj",
      interestOptions: ["Investiție în Energie Regenerabilă", "Finanțare Proiecte", "BESS / Gigafabrică", "Parteneriat", "Altele"],
      send: "Trimite Mesaj",
      success: "Mesaj trimis! Echipa noastră vă va contacta în 24 de ore.",
    },
    footer: {
      tagline: "Pionieri ai tranziției energetice în Europa Centrală și de Est.",
      links: { company: "Companie", investors: "Investitori", legal: "Legal" },
      copyright: "© KÖBER Energy Holding. All rights reserved.",
    },
    cookieBanner: {
      title: "Folosim cookie-uri",
      body: "Folosim cookie-uri pentru a îmbunătăți experiența dvs., a analiza traficul și a personaliza conținutul. Puteți accepta toate, respinge cookie-urile neesențiale sau gestiona preferințele.",
      acceptAll: "Acceptă Tot",
      rejectAll: "Respinge Tot",
      manage: "Gestionează",
      save: "Salvează Preferințele",
      policyLink: "Politica Cookie",
      necessary: { title: "Necesare", desc: "Esențiale pentru funcționalitatea de bază a site-ului. Nu pot fi dezactivate." },
      analytics: { title: "Analiză", desc: "Ne ajută să înțelegem cum interacționează vizitatorii cu site-ul." },
      marketing: { title: "Marketing", desc: "Utilizate pentru conținut personalizat și măsurarea eficienței campaniilor." },
    },
    holyGrail: {
      hero: {
        label: "KÖBER Energy Holding — Tehnologie de Avangardă",
        title: "Sfântul Graal al Energiei Regenerabile",
        subtitle: "O unitate independentă de generare a energiei care livrează energie nelimitată, continuă, eficientă, accesibilă și curată — disponibilă 24/7 din momentul activării.",
      },
      mission: {
        label: "Descoperirea Noastră",
        title: "Am Rezolvat Ceea Ce Alții Nu Au Putut",
        body: "Noi, KÖBER ENERGY HOLDING SRL, am creat \u201ESfântul Graal\u201D al energiei regenerabile. Am abordat și rezolvat problemele majore cu care se confruntă tehnologiile actuale și am dezvoltat o unitate independentă de generare a energiei care furnizează energie nelimitată, continuă, eficientă, accesibilă și curată, pregătită pentru scalare la nivel de masă.",
        integration: "Unitatea poate fi implementată ca sistem standalone sau integrată cu soluții de stocare, furnizând energie curată constantă, disponibilă 24/7 din momentul în care începe să funcționeze.",
      },
      pillars: [
        { title: "Implementare Oriunde", body: "Unitatea noastră poate fi implementată oriunde — acoperișuri urbane, zone industriale sau cele mai îndepărtate locații.", icon: "Globe" },
        { title: "Medii Extreme", body: "Poate funcționa în medii extreme unde tehnologiile convenționale eșuează sau nu pot fi implementate.", icon: "Thermometer" },
        { title: "Energie Instantanee", body: "Începe să genereze electricitate din momentul activării. Zero timp de pregătire. Zero întârziere.", icon: "Zap" },
      ],
      specs: [
        { label: "Dimensiune", value: "1MWh", desc: "Container 20 ft + 3X Standalone BESS, dimensiune suplimentară cu stocare" },
        { label: "Garanție", value: "10 Ani", desc: "Garanție completă pentru toate componentele" },
        { label: "Funcționare", value: "Silențios", desc: "Impact acustic zero" },
        { label: "Mentenanță", value: "Minimală", desc: "Cost operațional minim" },
        { label: "Obiectiv Cost", value: "< €0.05/kWh", desc: "Obiectivul nostru de preț" },
        { label: "Tarif Curent", value: "€0.063/kWh", desc: "Per kWh — leasing operațional 10 ani" },
      ],
      applications: {
        label: "Aplicații",
        title: "Alimentăm Industriile Care Nu Se Pot Opri",
        subtitle: "Alimentare cu sarcină de bază 24/7 pentru cei mai exigenți consumatori de energie.",
        items: [
          { title: "Centre de Date & IA", body: "Alimentare continuă de înaltă densitate pentru facilități hyperscale AI și cloud computing. Disponibilitate 24/7 garantată, fără dependență de rețea." },
          { title: "Industrie Grea & Siderurgie", body: "Sarcină de bază fiabilă pentru oțelării, turnătorii și procese de fabricație intensiv energetice care nu tolerează întreruperi." },
          { title: "Stații EV & Echilibrare Rețea", body: "Infrastructură de încărcare independentă, implementabilă oriunde, plus soluții de echilibrare pentru operatorii de rețele naționale." },
        ],
      },
      environment: {
        label: "Impact asupra Mediului",
        title: "Impact Zero. Prin Proiectare.",
        items: [
          "Zero emisii CO₂", "Zero impact asupra mediului",
          "Fără utilizare de substanțe chimice", "Fără expunere la foc sau căldură",
          "Zero risc de explozie", "Mentenanță facilă",
          "Greutate redusă", "Instalare simplă",
        ],
      },
      grid: {
        title: "Libertate Off-Grid & Echilibrare Rețea",
        body: "Unitatea noastră este în esență o soluție off-grid proiectată pentru autoconsum, reducând semnificativ dependența de rețeaua națională. De asemenea, unitățile pot fi implementate ca soluții de echilibrare pentru operatorii de rețele.",
        aim: "Obiectivul nostru: reducerea costului energiei la sub €0.05/kWh.",
      },
      bess: "* Integrare BESS (Sistem de Stocare a Energiei în Baterii) disponibilă — pentru energie curată continuă și dispatchabilă 24/7.",
      cta: {
        title: "Interesat de Implementarea Unității Holy Grail?",
        subtitle: "Contactați echipa noastră pentru opțiuni de implementare, termeni de leasing și oportunități de parteneriat strategic.",
        btn: "Contactați-ne",
      },
    },
  },
};
